import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { ShoppingBag, X } from "lucide-react";
import { CartLineItem } from "@/components/cart/CartLineItem";
import { FreeShippingBar } from "@/components/cart/FreeShippingBar";
import { useCart } from "@/context/CartContext";
import { useUI } from "@/context/UIContext";
import { formatINR } from "@/lib/format";

const primaryBtn = "bg-ink text-[11px] font-medium uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold";

function EmptyBag({ onShop }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" data-testid="cart-drawer-empty">
      <ShoppingBag className="h-10 w-10 text-gold" strokeWidth={1.2} />
      <p className="font-serif text-2xl text-ink">Your bag is empty</p>
      <p className="text-sm text-muted-foreground">Discover pieces made to make you shine.</p>
      <button onClick={onShop} className={`mt-2 px-8 py-3.5 ${primaryBtn}`}>Continue Shopping</button>
    </div>
  );
}

function DrawerFooter({ subtotal, onCheckout, onClose }) {
  return (
    <div className="border-t border-border px-6 py-5">
      <div className="flex items-center justify-between">
        <span className="text-luxe text-xs text-ink">Subtotal</span>
        <span className="font-serif text-2xl text-ink" data-testid="cart-drawer-subtotal">{formatINR(subtotal)}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Shipping & taxes calculated at checkout.</p>
      <button onClick={onCheckout} data-testid="cart-checkout-button" className={`mt-4 w-full py-4 ${primaryBtn}`}>View Bag & Checkout</button>
      <button onClick={onClose} className="mt-3 w-full text-center text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-gold">Continue Shopping</button>
    </div>
  );
}

export function CartDrawer() {
  const { items, subtotal, count, updateQuantity, removeItem, lineKey } = useCart();
  const { cartOpen, setCartOpen } = useUI();
  const navigate = useNavigate();

  const go = (path) => {
    setCartOpen(false);
    navigate(path);
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 border-l border-border bg-ivory p-0 sm:max-w-md [&>button]:hidden" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Shopping bag</SheetTitle>
        <SheetDescription className="sr-only">Review the items in your shopping bag</SheetDescription>
        <div className="flex items-center justify-between border-b border-border px-6 py-5">
          <h2 className="font-serif text-xl text-ink">
            Shopping Bag <span className="text-base text-muted-foreground">({count})</span>
          </h2>
          <button onClick={() => setCartOpen(false)} aria-label="Close cart" data-testid="cart-drawer-close" className="text-ink transition-colors hover:text-gold">
            <X size={20} />
          </button>
        </div>

        {items.length === 0 ? (
          <EmptyBag onShop={() => go("/shop")} />
        ) : (
          <>
            <FreeShippingBar subtotal={subtotal} />
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ul className="divide-y divide-border">
                {items.map((it) => {
                  const id = lineKey(it.product.handle, it.variantId);
                  return <CartLineItem key={id} item={it} lineId={id} onQuantity={updateQuantity} onRemove={removeItem} />;
                })}
              </ul>
            </div>
            <DrawerFooter subtotal={subtotal} onCheckout={() => go("/cart")} onClose={() => setCartOpen(false)} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
